import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Loader2, TrendingUp, TrendingDown } from 'lucide-react'
import { useIncreasingTopics, useDecreasingTopics } from '../hooks/usePYQAnalytics'
import { usePYQAnalyticsStore } from '../stores/pyqAnalyticsStore'
import { SUBJECT_COLORS, TREND_COLORS } from '../constants/colors'
import type { TrendingTopic } from '../types'
import { useActivationAccess, AnalyticsLockedOverlay } from '@/features/payments'
import { fbTrackViewContent } from '@/shared/utils/facebookPixel'

// Helper to get subject color
function getSubjectColor(subjectCode?: string): string {
  const code = (subjectCode || '').toUpperCase()
  if (code.includes('PHY')) return SUBJECT_COLORS.NEET_UG_PHY
  if (code.includes('CHEM')) return SUBJECT_COLORS.NEET_UG_CHEM
  if (code.includes('BIO')) return SUBJECT_COLORS.NEET_UG_BIO
  return '#6B7280'
}

// Helper to get subject name
function getSubjectName(subjectCode?: string): string {
  const code = (subjectCode || '').toUpperCase()
  if (code.includes('PHY')) return 'Physics'
  if (code.includes('CHEM')) return 'Chemistry'
  if (code.includes('BIO')) return 'Biology'
  return 'Unknown'
}

interface TopicCardProps {
  topic: TrendingTopic
  type: 'increasing' | 'decreasing'
  onClick: () => void
}

function TopicCard({ topic, type, onClick }: TopicCardProps) {
  const isIncreasing = type === 'increasing'
  const trendColor = isIncreasing ? TREND_COLORS.INCREASING : TREND_COLORS.DECREASING
  const TrendIcon = isIncreasing ? TrendingUp : TrendingDown

  // Convert trend coefficient to percentage (coefficient is typically between -1 and 1)
  const trendPercentage = Math.abs(topic.trendCoefficient * 100)

  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-all"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full shrink-0"
              style={{
                backgroundColor: getSubjectColor(topic.subjectCode) + '20',
                color: getSubjectColor(topic.subjectCode),
              }}
            >
              {getSubjectName(topic.subjectCode)}
            </span>
          </div>
          <h3 className="font-medium text-gray-900 line-clamp-2">{topic.topicName}</h3>
        </div>
        <div className="flex flex-col items-end shrink-0">
          <div
            className="flex items-center gap-1 px-2 py-1 rounded-full text-sm font-medium"
            style={{ backgroundColor: trendColor + '20', color: trendColor }}
          >
            <TrendIcon className="w-4 h-4" />
            <span>{trendPercentage.toFixed(0)}%</span>
          </div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-center">
        <div>
          <p className="text-xs text-gray-500">Total Questions</p>
          <p className="text-sm font-semibold text-gray-900">{topic.totalQuestions}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Importance</p>
          <p className="text-sm font-semibold text-gray-900">{topic.importanceScore.toFixed(1)}</p>
        </div>
      </div>
    </button>
  )
}

export default function TrendingTopicsPage() {
  const navigate = useNavigate()
  const { yearRangeStart, yearRangeEnd } = usePYQAnalyticsStore()
  const [activeTab, setActiveTab] = useState<'all' | 'increasing' | 'decreasing'>('all')
  const { isPaidUser } = useActivationAccess()

  // Track ViewContent for FB Pixel
  useEffect(() => {
    fbTrackViewContent('trending_topics', undefined, 'Trending Topics')
  }, [])

  const {
    data: increasingData,
    isLoading: isIncreasingLoading,
  } = useIncreasingTopics({
    yearRangeStart,
    yearRangeEnd,
    limit: 50,
  })

  const {
    data: decreasingData,
    isLoading: isDecreasingLoading,
  } = useDecreasingTopics({
    yearRangeStart,
    yearRangeEnd,
    limit: 50,
  })

  const isLoading = isIncreasingLoading || isDecreasingLoading
  const increasingTopics = increasingData?.topics || []
  const decreasingTopics = decreasingData?.topics || []

  const handleTopicClick = (topicCode: string) => {
    navigate(`/analytics/past-exams/topic/${topicCode}`)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-semibold text-gray-900">
                Trending Topics
              </h1>
              <p className="text-sm text-gray-500">
                Topics with changing frequency in recent exams
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'all'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setActiveTab('increasing')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === 'increasing'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              Rising ({increasingTopics.length})
            </button>
            <button
              onClick={() => setActiveTab('decreasing')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-1.5 ${
                activeTab === 'decreasing'
                  ? 'border-red-600 text-red-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <TrendingDown className="w-4 h-4" />
              Declining ({decreasingTopics.length})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : !isPaidUser ? (
          <AnalyticsLockedOverlay>
            <ContentSection
              activeTab={activeTab}
              increasingTopics={increasingTopics}
              decreasingTopics={decreasingTopics}
              onTopicClick={handleTopicClick}
            />
          </AnalyticsLockedOverlay>
        ) : (
          <ContentSection
            activeTab={activeTab}
            increasingTopics={increasingTopics}
            decreasingTopics={decreasingTopics}
            onTopicClick={handleTopicClick}
          />
        )}
      </div>
    </div>
  )
}

interface ContentSectionProps {
  activeTab: 'all' | 'increasing' | 'decreasing'
  increasingTopics: TrendingTopic[]
  decreasingTopics: TrendingTopic[]
  onTopicClick: (topicCode: string) => void
}

function ContentSection({ activeTab, increasingTopics, decreasingTopics, onTopicClick }: ContentSectionProps) {
  return (
    <>
      {/* All View - Two Columns */}
      {activeTab === 'all' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Rising Topics */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Rising Topics</h2>
              <span className="text-sm text-gray-500">({increasingTopics.length})</span>
            </div>
            <div className="space-y-3">
              {increasingTopics.length > 0 ? (
                increasingTopics.slice(0, 10).map((topic) => (
                  <TopicCard
                    key={topic.topicCode}
                    topic={topic}
                    type="increasing"
                    onClick={() => onTopicClick(topic.topicCode)}
                  />
                ))
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                  No rising topics found
                </div>
              )}
            </div>
          </div>

          {/* Declining Topics */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingDown className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-semibold text-gray-900">Declining Topics</h2>
              <span className="text-sm text-gray-500">({decreasingTopics.length})</span>
            </div>
            <div className="space-y-3">
              {decreasingTopics.length > 0 ? (
                decreasingTopics.slice(0, 10).map((topic) => (
                  <TopicCard
                    key={topic.topicCode}
                    topic={topic}
                    type="decreasing"
                    onClick={() => onTopicClick(topic.topicCode)}
                  />
                ))
              ) : (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
                  No declining topics found
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Rising Only View */}
      {activeTab === 'increasing' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {increasingTopics.length > 0 ? (
            increasingTopics.map((topic) => (
              <TopicCard
                key={topic.topicCode}
                topic={topic}
                type="increasing"
                onClick={() => onTopicClick(topic.topicCode)}
              />
            ))
          ) : (
            <div className="col-span-full bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              No rising topics found
            </div>
          )}
        </div>
      )}

      {/* Declining Only View */}
      {activeTab === 'decreasing' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {decreasingTopics.length > 0 ? (
            decreasingTopics.map((topic) => (
              <TopicCard
                key={topic.topicCode}
                topic={topic}
                type="decreasing"
                onClick={() => onTopicClick(topic.topicCode)}
              />
            ))
          ) : (
            <div className="col-span-full bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              No declining topics found
            </div>
          )}
        </div>
      )}
    </>
  )
}
